import React, { useEffect, useMemo, useRef } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { canTakeTest } from '@pages/ProductRegistration/AppropriatenessTest/appropriatenessTestStatus'
import { useProductRegistrationExit } from '@pages/ProductRegistration/hooks/useProductRegistrationExit'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import {
  hasProductValidationDeniedStatus,
  isEconomicProfileTestNeeded,
  showApptestRiskScreen,
} from '@utils/AccountAccess/accountProductStatuses'
import { getProductAppTestRiskAcceptancePath } from '@utils/path'

import { TestAnswerOutcomeStatus, TestStatusEnum } from '@model/ClientTestAnswerDto'
import { TestFormValues } from '@model/Test'
import { TickmillProductType } from '@model/TickmillProductType'

import { useTryProduct } from '@hooks/useTryProduct'

import { useScrollFullScreenToTop } from '@shared/templates/FullScreenLayout'
import { TestForm, TestFormRef } from '@shared/templates/Test/TestForm'
import { TestTemplate } from '@shared/templates/TestTemplate'

import { useProductAppropriatenessTest } from './hooks/useProductAppropriatenessTest'
import { useResolvedAppTestProduct } from './hooks/useResolvedAppTestProduct'

export const ProductAppropriatenessTestPage: React.FC = () => {
  const navigate = useNavigate()
  const { account } = useAccountReadContext()
  const { refreshAccount } = useAccountWriteContext()
  const resolvedProductId = useResolvedAppTestProduct()
  const { addProduct } = useTryProduct(resolvedProductId)
  const locale = useSessionLanguage()
  const handleExit = useProductRegistrationExit(resolvedProductId)

  const {
    isLoading,
    mutation,
    accountAppTest,
    formValues,
    step,
    handleSubmit,
    handleNextStep,
    accountAppTestAnswers,
    handlePrevStep,
  } = useProductAppropriatenessTest(resolvedProductId)
  useScrollFullScreenToTop([step])
  const contentRef = useRef<HTMLDivElement>(null)
  const childRef = useRef<TestFormRef>(null)
  const hasAddedProductRef = useRef(false)

  const handleChangeStepNext = (vals: TestFormValues) => {
    if (vals) handleNextStep(vals)
  }

  const handleChangeStepPrev = (vals: TestFormValues) => {
    if (vals) handlePrevStep(vals)
  }

  const isFirstSection = useMemo(() => {
    const firstSection = accountAppTest?.sections[0]
    return firstSection?.code === step
  }, [accountAppTest, step])

  useEffect(() => {
    if (hasAddedProductRef.current) return

    hasAddedProductRef.current = true
    void addProduct()
  }, [addProduct])

  useEffect(() => {
    const latestAnswers = mutation.accountApTestAnswers ?? accountAppTestAnswers
    const isTakeTestAllowed = canTakeTest(account, latestAnswers, resolvedProductId)
    const outcome = latestAnswers?.outcome?.outcome
    const isFailedOutcome = outcome === TestAnswerOutcomeStatus.FAILED
    const isMarginalPass =
      outcome === TestAnswerOutcomeStatus.REFERRED || outcome === TestAnswerOutcomeStatus.MARGINAL
    const isSQFProduct = resolvedProductId === TickmillProductType.SQF
    const requiresEconomicProfile = isEconomicProfileTestNeeded(account, resolvedProductId)
    const hasDeclinedRisk =
      isSQFProduct && isMarginalPass && hasProductValidationDeniedStatus(account, resolvedProductId)

    if (!account) return

    if (latestAnswers?.isClientVulnerable) {
      navigate('./denied', { replace: true })
    } else if (hasDeclinedRisk) {
      navigate('./risk-declined')
    } else if (showApptestRiskScreen(account, resolvedProductId)) {
      navigate(
        requiresEconomicProfile ? getProductAppTestRiskAcceptancePath(resolvedProductId) : './risk'
      )
    } else if (mutation.isFailed || (isFailedOutcome && (!isTakeTestAllowed || isSQFProduct))) {
      if (mutation.accountApTestAnswers?.isClientVulnerable) {
        navigate('./denied')
      } else {
        navigate('./failed')
      }
    } else if (
      !isTakeTestAllowed &&
      (mutation.isSuccess ||
        (latestAnswers &&
          !mutation.isFailed &&
          latestAnswers?.testStatus.id !== TestStatusEnum.Declined))
    ) {
      navigate('./success')
    }
  }, [
    account,
    accountAppTestAnswers,
    mutation.accountApTestAnswers,
    mutation.isFailed,
    mutation.isSuccess,
    navigate,
    resolvedProductId,
  ])

  const handleSubmission = async (values: TestFormValues) => {
    await handleSubmit(values)
    await refreshAccount(locale)
    return
  }

  const title = accountAppTest?.sections.find((section) => section.code === step)?.name

  return (
    <TestTemplate
      maxWidth={456}
      isLoading={isLoading}
      contentRef={contentRef}
      isFirstSection={isFirstSection}
      handleChangeStepPrev={() => childRef.current?.goPrevStep()}
      onExit={handleExit}
      title={title}
    >
      {accountAppTest && (
        <TestForm
          ref={childRef}
          noTitle
          data={accountAppTest}
          step={step}
          contentRef={contentRef}
          centered
          values={formValues}
          goPrevStep={handleChangeStepPrev}
          goNextStep={handleChangeStepNext}
          onSubmit={handleSubmission}
        />
      )}
    </TestTemplate>
  )
}
